// Ekstraøvelse: målrettet træning af svage taster, kombinationer og ord.

import { LEVEL_6_VALID } from '../data/curriculum/grundniveau';
import { NIVEAU_1_SPRINT_WORDS } from '../data/curriculum/niveau-1';
import { NIVEAU_2_SPRINT_WORDS } from '../data/curriculum/niveau-2';
import { NIVEAU_3_SPRINT_WORDS } from '../data/curriculum/niveau-3';
import { NIVEAU_4_ALPHABET_WORDS } from '../data/curriculum/niveau-4';
import {
  filterWords,
  fromChunks,
  fromLetters,
  literal,
  round,
} from '../data/curriculum/generators';
import { getCumulativeUnlockedKeys, textUsesOnlyKeys } from './dictationUnits';
import {
  analyzeWeakSpots,
  formatWeakSpotKeysLabel,
  isWeakSpotsEligible,
  MIN_KEY_SCORE,
} from './typingProfile';
import type { Trin, UserProfile } from '../types';

export const WEAK_SPOTS_TRIN_ID = '__weak_spots__';

export {
  analyzeWeakSpots,
  formatWeakSpotKeysLabel,
  isWeakSpotsEligible,
} from './typingProfile';

const TIER_CHUNKS: Record<string, string[]> = {
  grundniveau: LEVEL_6_VALID,
  'niveau-1': NIVEAU_1_SPRINT_WORDS,
  'niveau-2': NIVEAU_2_SPRINT_WORDS,
  'niveau-3': NIVEAU_3_SPRINT_WORDS,
  'niveau-4': NIVEAU_4_ALPHABET_WORDS,
};

const TIER_ORDER = [
  'grundniveau',
  'niveau-1',
  'niveau-2',
  'niveau-3',
  'niveau-4',
] as const;

function cumulativeWordPool(user: UserProfile): string[] {
  const allowed = getCumulativeUnlockedKeys(user);
  const seen = new Set<string>();
  const out: string[] = [];

  for (const tier of TIER_ORDER) {
    if (!user.completedTierIds.includes(tier)) continue;
    const chunks = TIER_CHUNKS[tier];
    if (!chunks) continue;
    for (const word of filterWords(chunks, allowed)) {
      if (seen.has(word)) continue;
      seen.add(word);
      out.push(word);
    }
  }

  return out;
}

function fallbackWordPool(
  user: UserProfile,
  weakKeys: ReadonlyArray<string>,
): string[] {
  const weakSet = new Set(weakKeys);
  if (weakSet.size === 0) return [];

  const pool = cumulativeWordPool(user);
  return pool
    .map((word) => {
      let score = 0;
      const seen = new Set<string>();
      for (const ch of word) {
        if (!weakSet.has(ch) || seen.has(ch)) continue;
        seen.add(ch);
        score += (user.errorsByKey[ch] ?? 0) * 2 + 3;
      }
      return { word, score };
    })
    .filter(({ score }) => score > 0)
    .sort((a, b) => b.score - a.score)
    .map(({ word }) => word);
}

function bigramDrillText(bigrams: string[], length: number): string {
  if (bigrams.length === 0) return '';
  const out: string[] = [];
  let chars = 0;
  let i = 0;
  while (chars < length) {
    const bg = bigrams[i % bigrams.length]!;
    out.push(bg);
    chars += bg.length + 1;
    i += 1;
  }
  return out.join(' ').trim();
}

export function buildWeakSpotsTrin(user: UserProfile): Trin | null {
  if (!isWeakSpotsEligible(user)) return null;

  const unlockedKeys = getCumulativeUnlockedKeys(user);
  const letterKeys = unlockedKeys.filter((k) => k !== ' ');
  if (letterKeys.length === 0) return null;

  const analysis = analyzeWeakSpots(user);
  const weakKeys = analysis.keys
    .filter((k) => k.score >= MIN_KEY_SCORE)
    .map((k) => k.key);
  const focusLabel = formatWeakSpotKeysLabel(user);

  const rounds = [];

  const safeBigrams = analysis.drillBigrams.filter((s) =>
    textUsesOnlyKeys(s, unlockedKeys),
  );

  if (safeBigrams.length > 0) {
    const drill = bigramDrillText(safeBigrams, 80);
    rounds.push(
      round({
        id: `${WEAK_SPOTS_TRIN_ID}-r1`,
        title: 'Kombinationer',
        purpose: 'Dine svageste tast-overgange — gentaget indtil de sidder.',
        mode: 'recall',
        generate: literal(drill),
      }),
    );
  } else {
    rounds.push(
      round({
        id: `${WEAK_SPOTS_TRIN_ID}-r1`,
        title: 'Kombinationer',
        purpose: 'Korte grupper med dine svageste taster.',
        mode: 'recall',
        generate: fromLetters(letterKeys, 2, 80),
      }),
    );
  }

  const profileWords = filterWords(analysis.drillWords, unlockedKeys);
  const words =
    profileWords.length > 0
      ? profileWords
      : fallbackWordPool(user, weakKeys);

  if (words.length > 0) {
    rounds.push(
      round({
        id: `${WEAK_SPOTS_TRIN_ID}-r2`,
        title: 'Ord der driller',
        purpose: 'Ord du ofte fejler på eller skriver langsomt.',
        mode: 'recall',
        generate: fromChunks(words.slice(0, 40), 70),
      }),
    );
  }

  return {
    id: WEAK_SPOTS_TRIN_ID,
    tierId: 'grundniveau',
    trinNumber: 0,
    title: 'Svage punkter',
    description: focusLabel
      ? `Målrettet træning: ${focusLabel}.`
      : 'Målrettet træning af det du fejler på eller tøver på.',
    goal: 'Styrk de taster, kombinationer og ord der stadig koster ekstra fokus.',
    unlockedKeys,
    newKeys: [],
    rounds,
  };
}
